import { db, handleFirestoreError, OperationType, isQuotaError } from '../firebase';
import { 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  getDoc,
  writeBatch, 
  query, 
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { AcademicYear, ClassYearConfig, Student, StudentEnrollment } from '../types';
import { dataCache, getCachedQuery, getCachedCollection } from './dataCacheService';
import { clearDailyStatsCache } from './statsService';

export const DEFAULT_ACADEMIC_YEARS: AcademicYear[] = [
  { id: '2026-2027', name: 'תשפ״ז (2026-2027)', isDefault: true, startDate: '2026-09-01', endDate: '2027-08-31' },
  { id: '2025-2026', name: 'תשפ״ו (2025-2026)', isDefault: false, startDate: '2025-09-01', endDate: '2026-08-31' },
  { id: '2027-2028', name: 'תשפ״ח (2027-2028)', isDefault: false, startDate: '2027-09-01', endDate: '2028-08-31' },
  { id: '2024-2025', name: 'תשפ״ה (2024-2025)', isDefault: false, startDate: '2024-09-01', endDate: '2025-08-31' }
];

/**
 * Returns start and end dates strictly according to the academic year definition:
 * From 1.9 of the first year to 31.8 of the following year.
 * E.g. '2026-2027' -> startDate: '2026-09-01', endDate: '2027-08-31'
 */
export function getAcademicYearRange(yearId: string): { startDate: string; endDate: string } {
  if (!yearId) return { startDate: '2026-09-01', endDate: '2027-08-31' };
  const parts = yearId.split('-');
  if (parts.length === 2) {
    const startYear = parseInt(parts[0], 10);
    const endYear = parseInt(parts[1], 10);
    if (!isNaN(startYear) && !isNaN(endYear)) {
      return {
        startDate: `${startYear}-09-01`,
        endDate: `${endYear}-08-31`
      };
    }
  }
  return { startDate: '2026-09-01', endDate: '2027-08-31' };
}

/**
 * Determines which academic year a specific date belongs to based on the 1.9 to 31.8 rule:
 * Dates from September 1 to December 31 belong to YYYY-(YYYY+1).
 * Dates from January 1 to August 31 belong to (YYYY-1)-YYYY.
 */
export function getAcademicYearForDate(dateStr: string): string {
  if (!dateStr) return '2026-2027';
  const parts = dateStr.split('-');
  if (parts.length < 2) return '2026-2027';
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  if (isNaN(year) || isNaN(month)) return '2026-2027';

  if (month >= 9) {
    return `${year}-${year + 1}`;
  } else {
    return `${year - 1}-${year}`;
  }
}

/**
 * Checks whether an absence belongs to a given academic year.
 * An absence overlaps if its date range overlaps with [YYYY-09-01, (YYYY+1)-08-31].
 */
export function isAbsenceInAcademicYear(
  absence: { startDate: string; endDate?: string; academicYear?: string },
  yearId: string
): boolean {
  if (!yearId || yearId === 'all') return true;
  
  const { startDate: yStart, endDate: yEnd } = getAcademicYearRange(yearId);
  const aStart = absence.startDate;
  const aEnd = absence.endDate || absence.startDate;

  if (!aStart && !aEnd) return false;
  
  // If the document explicitly has academicYear set and matches, return true
  if (absence.academicYear && absence.academicYear === yearId) {
    return true;
  }

  // Check date range overlap with the academic year (01/09 to 31/08)
  const effectiveStart = aStart || aEnd;
  const effectiveEnd = aEnd || aStart;
  return effectiveEnd >= yStart && effectiveStart <= yEnd;
}

/**
 * Fetch available academic years. Defaults to predefined years if not in Firestore.
 * Always ensures startDate and endDate are guaranteed to be 1.9 to 31.8.
 */
export async function getAcademicYears(): Promise<AcademicYear[]> {
  try {
    const q = query(collection(db, 'academicYears'));
    const docs = await getCachedQuery<AcademicYear>('academic_years_list', q, false, 30 * 60 * 1000);
    if (docs && docs.length > 0) {
      return docs.map(y => {
        const range = getAcademicYearRange(y.id);
        return {
          ...y,
          startDate: y.startDate || range.startDate,
          endDate: y.endDate || range.endDate
        };
      });
    }
  } catch (err) {
    console.warn("Could not fetch academic years from Firestore, using defaults:", err);
  }
  return DEFAULT_ACADEMIC_YEARS;
}

/**
 * Normalizes class names for robust comparison and grouping across all views.
 * Strips common prefixes like "כיתת", "כיתה", "גן" and quotes.
 */
export function normalizeClassName(name: string | undefined | null): string {
  if (!name) return '';
  return name
    .trim()
    .toLowerCase()
    .replace(/^כיתת\s+/, '')
    .replace(/^כיתה\s+/, '')
    .replace(/^גן\s+/, '')
    .replace(/["'״׳]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Checks whether two class references refer to the same class
 * (by exact ID match, exact name match, or normalized alias/nickname match).
 */
export function isSameClass(
  classRefA: { id?: string; name?: string } | string | null | undefined,
  classRefB: { id?: string; name?: string } | string | null | undefined
): boolean {
  if (!classRefA || !classRefB) return false;
  const idA = typeof classRefA === 'string' ? classRefA : classRefA.id || '';
  const nameA = typeof classRefA === 'string' ? classRefA : classRefA.name || '';
  const idB = typeof classRefB === 'string' ? classRefB : classRefB.id || '';
  const nameB = typeof classRefB === 'string' ? classRefB : classRefB.name || '';

  if (idA && idB && idA === idB) return true;
  if (nameA && nameB && nameA.trim().toLowerCase() === nameB.trim().toLowerCase()) return true;

  const normA = normalizeClassName(nameA || idA);
  const normB = normalizeClassName(nameB || idB);
  if (normA && normB) {
    if (normA === normB) return true;

    // Check if one contains the other, but make sure they don't have conflicting section letters/numbers
    // E.g. "8.א" vs "8.ב" or "1" vs "2" must never match!
    const extractSection = (str: string) => {
      const match = str.match(/([0-9]+[.\s]*[א-תa-zA-Z]|[א-תa-zA-Z][.\s]*[0-9]+|[0-9]+)/);
      return match ? match[0].replace(/\s+/g, '') : null;
    };
    const secA = extractSection(normA);
    const secB = extractSection(normB);
    if (secA && secB && secA !== secB) {
      return false;
    }

    if (normA.includes(normB) || normB.includes(normA)) return true;
  }
  return false;
}

/**
 * Resolves which class a student belongs to for a specific academic year.
 * For 2025-2026: Falls back to legacy student.classId if no enrollment record exists.
 * For 2026-2027 and newer: Returns null if the student has NOT been enrolled for this year yet!
 * Returns null if the student is inactive globally or in the specified year.
 */
export function getStudentClassForYear(student: Student, year: string): { classId: string; className: string } | null {
  if (!student) return null;
  if (student.active === false || (student as any).isActive === false || (student as any).status === 'inactive') return null;

  // 1. Check explicit enrollment for this year
  if (student.enrollments && student.enrollments[year]) {
    const enrollment = student.enrollments[year];
    if (enrollment.active === false || (enrollment as any).isActive === false || (enrollment as any).status === 'inactive') {
      return null;
    }
    if (enrollment.classId || enrollment.className) {
      return {
        classId: enrollment.classId || enrollment.className || '',
        className: enrollment.className || enrollment.classId || ''
      };
    }
    return null; // Explicitly marked as not active / unenrolled for this year
  }

  // 2. Legacy fallback for 2025-2026 (the original year the system was built for)
  if (year === '2025-2026') {
    if (student.classId || student.className) {
      return {
        classId: student.classId || student.className || '',
        className: student.className || student.classId || ''
      };
    }
  }

  // 3. For any other year (including 2026-2027), they are not enrolled unless explicitly assigned!
  return null;
}

/**
 * Returns whether a student is enrolled in the given academic year.
 */
export function isStudentEnrolledInYear(student: Student, year: string): boolean {
  return getStudentClassForYear(student, year) !== null;
}

/**
 * Fetches class configurations (kindergarten manager, assistant, notes) for a specific year.
 */
export async function getClassYearConfigs(year: string): Promise<Record<string, ClassYearConfig>> {
  const configsMap: Record<string, ClassYearConfig> = {};
  try {
    const q = query(collection(db, 'classAcademicYears'), where('year', '==', year));
    const docs = await getCachedQuery<ClassYearConfig>(`class_academic_years_${year}`, q, false, 15 * 60 * 1000);
    if (docs) {
      docs.forEach(doc => {
        if (doc.classId) {
          configsMap[doc.classId] = doc;
        }
      });
    }
  } catch (err) {
    console.warn(`Error fetching class configs for year ${year}:`, err);
  }
  return configsMap;
}

/**
 * Saves or updates a class configuration (such as kindergarten manager / teacher) for a specific year.
 */
export async function saveClassYearConfig(
  year: string,
  classId: string,
  className: string,
  data: Partial<ClassYearConfig>
): Promise<void> {
  const docId = `${year}_${classId}`;
  const ref = doc(db, 'classAcademicYears', docId);

  const payload: ClassYearConfig = {
    id: docId,
    year,
    classId,
    className,
    teacherName: data.teacherName || '',
    teacherPhone: data.teacherPhone || '',
    assistantName: data.assistantName || '',
    assistantPhone: data.assistantPhone || '',
    notes: data.notes || '',
    updatedAt: serverTimestamp()
  };

  await setDoc(ref, payload, { merge: true });
  dataCache.remove(`class_academic_years_${year}`);
}

/**
 * Enrolls a single student into a class for a specific year.
 */
export async function enrollStudentInYear(
  studentId: string,
  year: string,
  classId: string,
  className: string,
  additionalDetails?: Partial<StudentEnrollment>
): Promise<void> {
  const studentRef = doc(db, 'students', studentId);
  const enrollment: StudentEnrollment = {
    classId,
    className,
    active: true,
    enrolledAt: new Date().toISOString(),
    ...additionalDetails
  };

  await setDoc(studentRef, {
    classId: classId || '',
    className: className || '',
    active: true,
    enrollments: {
      [year]: enrollment
    }
  }, { merge: true });

  dataCache.remove('students');
  clearDailyStatsCache(year);
}

/**
 * Bulk enrolls a list of student IDs into a class for a specific year.
 */
export async function bulkEnrollStudentsInYear(
  studentIds: string[],
  year: string,
  classId: string,
  className: string
): Promise<number> {
  if (!studentIds.length) return 0;

  // Process in chunks of 450 (Firestore limit is 500 per batch)
  const chunkSize = 400;
  let enrolledCount = 0;

  for (let i = 0; i < studentIds.length; i += chunkSize) {
    const chunk = studentIds.slice(i, i + chunkSize);
    const batch = writeBatch(db);

    chunk.forEach(id => {
      const ref = doc(db, 'students', id);
      batch.set(ref, {
        classId: classId || '',
        className: className || '',
        active: true,
        enrollments: {
          [year]: {
            classId,
            className,
            active: true,
            enrolledAt: new Date().toISOString()
          }
        }
      }, { merge: true });
    });

    await batch.commit();
    enrolledCount += chunk.length;
  }

  dataCache.remove('students');
  clearDailyStatsCache(year);
  return enrolledCount;
}

/**
 * Unenrolls a student from a specific academic year.
 */
export async function unenrollStudentFromYear(studentId: string, year: string): Promise<void> {
  const studentRef = doc(db, 'students', studentId);
  await setDoc(studentRef, {
    enrollments: {
      [year]: {
        classId: '',
        className: '',
        active: false,
        unenrolledAt: new Date().toISOString()
      }
    }
  }, { merge: true });

  dataCache.remove('students');
  clearDailyStatsCache(year);
}

/**
 * Resolves transport line and escort for a student for a specific academic year.
 */
export function getStudentTransportForYear(student: Student, year: string): { transportName: string; escortName: string } {
  if (!student) return { transportName: '', escortName: '' };
  const enrollment = student.enrollments?.[year];
  if (enrollment && enrollment.transportName !== undefined) {
    return {
      transportName: enrollment.transportName || '',
      escortName: enrollment.escortName || ''
    };
  }
  return {
    transportName: student.transportName || '',
    escortName: student.escortName || ''
  };
}

/**
 * Updates transport line and optional escort for a student for a specific academic year.
 */
export async function updateStudentTransportForYear(
  studentId: string,
  year: string,
  transportName: string,
  escortName?: string
): Promise<void> {
  const studentRef = doc(db, 'students', studentId);
  const snap = await getDoc(studentRef);
  const data = snap.data();
  const currentEnrollment = data?.enrollments?.[year] || {};

  await setDoc(studentRef, {
    transportName: transportName,
    ...(escortName !== undefined ? { escortName } : {}),
    enrollments: {
      [year]: {
        ...currentEnrollment,
        transportName: transportName,
        ...(escortName !== undefined ? { escortName } : {})
      }
    }
  }, { merge: true });

  dataCache.remove('students');
}

/**
 * Bulk assigns a transport line to multiple students for a specific academic year.
 */
export async function bulkAssignTransportForYear(
  studentIds: string[],
  year: string,
  transportName: string
): Promise<number> {
  if (!studentIds.length) return 0;

  const chunkSize = 400;
  let updatedCount = 0;

  for (let i = 0; i < studentIds.length; i += chunkSize) {
    const chunk = studentIds.slice(i, i + chunkSize);
    const batch = writeBatch(db);

    chunk.forEach(id => {
      const ref = doc(db, 'students', id);
      batch.set(ref, {
        transportName: transportName,
        enrollments: {
          [year]: {
            transportName: transportName
          }
        }
      }, { merge: true });
    });

    await batch.commit();
    updatedCount += chunk.length;
  }

  dataCache.remove('students');
  return updatedCount;
}
